/**
 * Lead Detail Page
 * Full lead detail page with tabs for overview, contact details, tour history, and enrollment info
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  User,
  Users,
  MoreVertical,
  ChevronRight,
  Plus,
  StickyNote,
  Home,
  Send,
  UserCheck,
  GraduationCap,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Heart
} from 'lucide-react';
import {
  useLead,
  useLeadActivities,
  useMoveLeadToNext,
  useMarkLeadAsLost,
  useAddNote,
  useAssignLead,
  useScheduleTour,
} from '../../hooks/useCRM';
import LeadActivity from '../../components/crm/LeadActivity';
import AddNoteModal from '../../components/crm/AddNoteModal';
import TourScheduleModal from '../../components/crm/TourScheduleModal';
import ConvertLeadModal from '../../components/crm/ConvertLeadModal';
import AssignLeadModal from '../../components/crm/AssignLeadModal';

// Lead pipeline stages
const STAGES = [
  { id: 'new', label: 'New', color: 'bg-gray-100 text-gray-700', description: 'New lead' },
  { id: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700', description: 'Initial contact made' },
  { id: 'tour_scheduled', label: 'Tour Scheduled', color: 'bg-purple-100 text-purple-700', description: 'Facility tour scheduled' },
  { id: 'tour_completed', label: 'Tour Completed', color: 'bg-indigo-100 text-indigo-700', description: 'Tour completed' },
  { id: 'follow_up', label: 'Follow-up', color: 'bg-yellow-100 text-yellow-700', description: 'Follow-up in progress' },
  { id: 'enrollment_offer', label: 'Enrollment Offer', color: 'bg-orange-100 text-orange-700', description: 'Enrollment offer sent' },
  { id: 'enrolled', label: 'Enrolled', color: 'bg-green-100 text-green-700', description: 'Successfully enrolled' },
  { id: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700', description: 'Lead lost' },
];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'contact', label: 'Contact Details' },
  { id: 'tours', label: 'Tour History' },
  { id: 'enrollment', label: 'Enrollment Info' },
];

const LeadDetail = () => {
  const { tenantId, leadId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showTourModal, setShowTourModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStageMenu, setShowStageMenu] = useState(false);
  const [showLostReasonModal, setShowLostReasonModal] = useState(false);
  const [lostReason, setLostReason] = useState('');

  // Fetch data
  const { data: lead, isLoading: isLoadingLead, error: leadError } = useLead(leadId);
  const { data: activities, isLoading: isLoadingActivities, error: activitiesError } = useLeadActivities(leadId);

  // Mutations
  const moveToNextMutation = useMoveLeadToNext();
  const markAsLostMutation = useMarkLeadAsLost();
  const addNoteMutation = useAddNote();
  const assignLeadMutation = useAssignLead();
  const scheduleTourMutation = useScheduleTour();

  if (isLoadingLead) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (leadError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        Failed to load lead: {leadError.message}
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
        Lead not found
      </div>
    );
  }

  const currentStage = STAGES.find(s => s.id === lead.stage) || STAGES[0];
  // eslint-disable-next-line no-unused-vars
  const _currentStageIndex = STAGES.findIndex(s => s.id === lead.stage);

  const handleStageChange = async (newStage) => {
    try {
      if (newStage === 'lost') {
        setShowLostReasonModal(true);
      } else {
        // Use moveLead mutation for specific stage
        // For now, we'll use a simple approach
      }
      setShowStageMenu(false);
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
  };

  const handleMoveToNext = async () => {
    try {
      await moveToNextMutation.mutateAsync(leadId);
    } catch (err) {
      console.error('Failed to move to next stage:', err);
    }
  };

  const handleMarkAsLost = async () => {
    try {
      await markAsLostMutation.mutateAsync({ id: leadId, reason: lostReason });
      setShowLostReasonModal(false);
      setLostReason('');
    } catch (err) {
      console.error('Failed to mark as lost:', err);
    }
  };

  const handleAddNote = async (data) => {
    try {
      await addNoteMutation.mutateAsync({ id: leadId, content: data.content });
      setShowAddNoteModal(false);
    } catch (err) {
      console.error('Failed to add note:', err);
    }
  };

  const handleScheduleTour = async (data) => {
    try {
      await scheduleTourMutation.mutateAsync({ id: leadId, ...data });
      setShowTourModal(false);
    } catch (err) {
      console.error('Failed to schedule tour:', err);
    }
  };

  const handleAssignLead = async (data) => {
    try {
      await assignLeadMutation.mutateAsync({ id: leadId, assigneeId: data.assigneeId });
      setShowAssignModal(false);
    } catch (err) {
      console.error('Failed to assign lead:', err);
    }
  };

  const handleConvertLead = async (data) => {
    // This would call a convert lead mutation
    console.log('Convert lead:', data);
    setShowConvertModal(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStageProgress = () => {
    if (lead.stage === 'enrolled') return 100;
    if (lead.stage === 'lost') return 0;
    const progressStages = ['new', 'contacted', 'tour_scheduled', 'tour_completed', 'follow_up', 'enrollment_offer'];
    const index = progressStages.indexOf(lead.stage);
    return index >= 0 ? ((index + 1) / progressStages.length) * 100 : 0;
  };

  const getAssignedStaffNames = () => {
    if (!lead.assignedTo || lead.assignedTo.length === 0) return 'Unassigned';
    // This would fetch actual staff names from the API
    return `${lead.assignedTo.length} staff member${lead.assignedTo.length > 1 ? 's' : ''}`;
  };

  // Header with lead info and actions
  const renderHeader = () => (
    <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        {/* Left: Lead Info */}
        <div className="flex items-start space-x-4">
          <button
            onClick={() => navigate(`/${tenantId}/dashboard/crm`)}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-2xl font-bold text-purple-700">
              {lead.childFirstName?.[0]}{lead.childLastName?.[0]}
            </span>
          </div>
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {lead.childFirstName} {lead.childLastName}
            </h1>
            <p className="text-gray-500">
              Parent: {lead.parentFirstName} {lead.parentLastName}
            </p>
            
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
              <span className="flex items-center">
                <Mail size={14} className="mr-1.5" />
                {lead.email}
              </span>
              {lead.phone && (
                <span className="flex items-center">
                  <Phone size={14} className="mr-1.5" />
                  {lead.phone}
                </span>
              )}
              <span className="flex items-center">
                <Clock size={14} className="mr-1.5" />
                Created {formatDate(lead.createdAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Stage Badge & Actions */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Stage Badge */}
          <div className="relative">
            <button
              onClick={() => setShowStageMenu(!showStageMenu)}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${currentStage.color} hover:opacity-90 transition-opacity`}
            >
              {currentStage.label}
              <ChevronRight size={16} className="ml-1" />
            </button>
            
            {showStageMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowStageMenu(false)} 
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 z-20 py-1">
                  {STAGES.map((stage) => (
                    <button
                      key={stage.id}
                      onClick={() => handleStageChange(stage.id)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between ${
                        stage.id === lead.stage ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                      }`}
                    >
                      <div>
                        <span className="font-medium">{stage.label}</span>
                        <p className="text-xs text-gray-500">{stage.description}</p>
                      </div>
                      {stage.id === lead.stage && (
                        <CheckCircle2 size={16} />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          {lead.stage !== 'enrolled' && lead.stage !== 'lost' && (
            <>
              <button
                onClick={() => setShowConvertModal(true)}
                className="btn-primary flex items-center space-x-2 bg-green-600 hover:bg-green-700"
              >
                <GraduationCap size={18} />
                <span>Convert</span>
              </button>
              
              <button
                onClick={handleMoveToNext}
                disabled={moveToNextMutation.isPending}
                className="btn-primary flex items-center space-x-2"
              >
                <ChevronRight size={18} />
                <span>Next Stage</span>
              </button>
              
              <button
                onClick={() => setShowLostReasonModal(true)}
                className="px-4 py-2 bg-white text-red-600 font-medium rounded-lg border border-red-200 hover:bg-red-50 transition-colors flex items-center space-x-2"
              >
                <XCircle size={18} />
                <span>Mark Lost</span>
              </button>
            </>
          )}
          
          <button className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <MoreVertical size={20} />
          </button>
        </div>
      </div>

      {/* Stage Progress */}
      <div className="mt-6 pt-6 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
          <span>Pipeline Progress</span>
          <span>{Math.round(getStageProgress())}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-purple-600 transition-all duration-500"
            style={{ width: `${getStageProgress()}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>New</span>
          <span>Contacted</span>
          <span>Tour</span>
          <span>Follow-up</span>
          <span>Offer</span>
          <span>Enrolled</span>
        </div>
      </div>
    </div>
  );

  // Quick Actions Bar
  const renderQuickActions = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-500 mr-2">Quick Actions:</span>
        
        <button
          onClick={() => setShowAddNoteModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
        >
          <StickyNote size={16} />
          <span>Add Note</span>
        </button>
        
        <button
          onClick={() => setShowTourModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium"
        >
          <Home size={16} />
          <span>Schedule Tour</span>
        </button>
        
        <a
          href={`mailto:${lead.email}`}
          className="flex items-center space-x-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors text-sm font-medium"
        >
          <Send size={16} />
          <span>Send Email</span>
        </a>
        
        <button
          onClick={() => setShowAssignModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
        >
          <UserCheck size={16} />
          <span>Assign</span>
        </button>
      </div>
    </div>
  );

  // Overview Tab Content
  const renderOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column: Lead Summary */}
      <div className="space-y-6">
        {/* Contact Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <User size={18} className="mr-2 text-purple-600" />
            Contact Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-start">
              <User size={16} className="text-gray-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Parent/Guardian</p>
                <p className="text-sm text-gray-900 font-medium">
                  {lead.parentFirstName} {lead.parentLastName}
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <Mail size={16} className="text-gray-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <a href={`mailto:${lead.email}`} className="text-sm text-purple-600 hover:underline">
                  {lead.email}
                </a>
              </div>
            </div>
            {lead.phone && (
              <div className="flex items-start">
                <Phone size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <a href={`tel:${lead.phone}`} className="text-sm text-gray-900">
                    {lead.phone}
                  </a>
                </div>
              </div>
            )}
            {lead.address && (
              <div className="flex items-start">
                <MapPin size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="text-sm text-gray-900">{lead.address}</p>
                </div>
              </div>
            )}
            {lead.source && (
              <div className="flex items-start">
                <ExternalLink size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Source</p>
                  <p className="text-sm text-gray-900 capitalize">{lead.source}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Student Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <GraduationCap size={18} className="mr-2 text-purple-600" />
            Student Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-start">
              <User size={16} className="text-gray-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Child Name</p>
                <p className="text-sm text-gray-900 font-medium">
                  {lead.childFirstName} {lead.childLastName}
                </p>
              </div>
            </div>
            {lead.childAge && (
              <div className="flex items-start">
                <Calendar size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Age</p>
                  <p className="text-sm text-gray-900">{lead.childAge} years old</p>
                </div>
              </div>
            )}
            {lead.childInterests && (
              <div className="flex items-start">
                <Heart size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Interests</p>
                  <p className="text-sm text-gray-900">{lead.childInterests}</p>
                </div>
              </div>
            )}
            {lead.programInterest && (
              <div className="flex items-start">
                <BookOpenIcon size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Program Interest</p>
                  <p className="text-sm text-gray-900">{lead.programInterest}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Assignment Card */}
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <UserCheck size={18} className="mr-2 text-purple-600" />
            Assignment
          </h3>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                <User size={18} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {getAssignedStaffNames()}
                </p>
                <p className="text-xs text-gray-500">Assigned staff</p>
              </div>
            </div>
            <button
              onClick={() => setShowAssignModal(true)}
              className="px-3 py-2 min-h-[44px] text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg font-medium"
            >
              Change
            </button>
          </div>
        </div>

        {/* Tour Info Card */}
        {lead.tourScheduledAt && (
          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Home size={18} className="mr-2 text-purple-600" />
              Scheduled Tour
            </h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <Calendar size={16} className="text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Date & Time</p>
                  <p className="text-sm text-gray-900">{formatDateTime(lead.tourScheduledAt)}</p>
                </div>
              </div>
              {lead.tourType && (
                <div className="flex items-start">
                  <Home size={16} className="text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Tour Type</p>
                    <p className="text-sm text-gray-900">{lead.tourType}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center mt-3">
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                  lead.tourStatus === 'completed' ? 'bg-green-100 text-green-700' :
                  lead.tourStatus === 'cancelled' ? 'bg-red-100 text-red-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {lead.tourStatus === 'completed' ? 'Completed' :
                   lead.tourStatus === 'cancelled' ? 'Cancelled' :
                   'Scheduled'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Column: Activity Timeline */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900 flex items-center">
              <MessageSquare size={18} className="mr-2 text-purple-600" />
              Activity Timeline
            </h3>
            <button
              onClick={() => setShowAddNoteModal(true)}
              className="px-3 py-2 min-h-[44px] text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 flex items-center font-medium rounded-lg"
            >
              <Plus size={16} className="mr-1" />
              Add Activity
            </button>
          </div>
          
          <LeadActivity 
            activities={activities}
            isLoading={isLoadingActivities}
            error={activitiesError}
          />
        </div>
      </div>
    </div>
  );

  // Contact Details Tab
  const renderContactDetails = () => (
    <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
      <h3 className="font-semibold text-gray-900 mb-6">Contact Details</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Parent Information */}
        <div>
          <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Parent/Guardian</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Full Name</label>
              <p className="text-gray-900 font-medium">{lead.parentFirstName} {lead.parentLastName}</p>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Email</label>
              <a href={`mailto:${lead.email}`} className="text-purple-600 hover:underline">
                {lead.email}
              </a>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Phone</label>
              <p className="text-gray-900">{lead.phone || 'Not provided'}</p>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Preferred Contact</label>
              <p className="text-gray-900 capitalize">{lead.preferredContact || 'Email'}</p>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div>
          <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Address</h4>
          {lead.address ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Street Address</label>
                <p className="text-gray-900">{lead.address}</p>
              </div>
              {lead.city && (
                <div>
                  <label className="block text-sm text-gray-500 mb-1">City</label>
                  <p className="text-gray-900">{lead.city}</p>
                </div>
              )}
              {(lead.state || lead.zipCode) && (
                <div className="grid grid-cols-2 gap-4">
                  {lead.state && (
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">State</label>
                      <p className="text-gray-900">{lead.state}</p>
                    </div>
                  )}
                  {lead.zipCode && (
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">ZIP Code</label>
                      <p className="text-gray-900">{lead.zipCode}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 italic">No address provided</p>
          )}
        </div>

        {/* Additional Information */}
        <div className="md:col-span-2">
          <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Additional Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Lead Source</label>
              <p className="text-gray-900 capitalize">{lead.source || 'Unknown'}</p>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Created</label>
              <p className="text-gray-900">{formatDate(lead.createdAt)}</p>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Last Updated</label>
              <p className="text-gray-900">{formatDate(lead.updatedAt)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Tour History Tab
  const renderTourHistory = () => {
    const tourActivities = activities?.filter(a => 
      a.type === 'tour_scheduled' || a.type === 'tour_completed' || a.type === 'tour_cancelled'
    ) || [];

    return (
      <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-gray-900">Tour History</h3>
          <button
            onClick={() => setShowTourModal(true)}
            className="text-sm text-purple-600 hover:text-purple-700 flex items-center font-medium"
          >
            <Plus size={16} className="mr-1" />
            Schedule New Tour
          </button>
        </div>
        
        {tourActivities.length > 0 ? (
          <div className="space-y-4">
            {tourActivities.map((tour) => (
              <div key={tour.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                      tour.type === 'tour_completed' ? 'bg-green-100' :
                      tour.type === 'tour_cancelled' ? 'bg-red-100' :
                      'bg-purple-100'
                    }`}>
                      <Home size={18} className={`${
                        tour.type === 'tour_completed' ? 'text-green-600' :
                        tour.type === 'tour_cancelled' ? 'text-red-600' :
                        'text-purple-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{tour.title}</p>
                      <p className="text-sm text-gray-500">
                        {formatDateTime(tour.scheduledAt || tour.createdAt)}
                      </p>
                      {tour.tourType && (
                        <p className="text-sm text-gray-600 mt-1">{tour.tourType}</p>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    tour.type === 'tour_completed' ? 'bg-green-100 text-green-700' :
                    tour.type === 'tour_cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {tour.type === 'tour_completed' ? 'Completed' :
                     tour.type === 'tour_cancelled' ? 'Cancelled' :
                     'Scheduled'}
                  </span>
                </div>
                
                {tour.staff && tour.staff.length > 0 && (
                  <div className="flex items-center mt-3 text-sm text-gray-600">
                    <Users size={14} className="mr-1" />
                    <span>Staff: {tour.staff.join(', ')}</span>
                  </div>
                )}
                
                {tour.content && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-gray-700">{tour.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Home className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-lg font-medium">No tours scheduled</p>
            <p className="text-sm mt-1">Schedule a tour to show the facility to this lead.</p>
            <button
              onClick={() => setShowTourModal(true)}
              className="mt-4 text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              Schedule a tour
            </button>
          </div>
        )}
      </div>
    );
  };

  // Enrollment Info Tab
  const renderEnrollmentInfo = () => (
    <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
      <h3 className="font-semibold text-gray-900 mb-6">Enrollment Information</h3>
      
      {lead.status === 'enrolled' ? (
        <div className="space-y-6">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle2 size={24} className="text-green-600 mr-3" />
              <div>
                <p className="font-medium text-green-900">Successfully Enrolled</p>
                <p className="text-sm text-green-700">
                  This lead was converted to a student on {formatDate(lead.enrolledAt)}
                </p>
              </div>
            </div>
          </div>
          
          {lead.studentId && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Student Record</h4>
              <button
                onClick={() => navigate(`/${tenantId}/dashboard/students/${lead.studentId}`)}
                className="flex items-center px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <GraduationCap size={20} className="mr-3" />
                <div className="text-left">
                  <p className="font-medium">View Student Profile</p>
                  <p className="text-sm opacity-75">Student ID: {lead.studentId}</p>
                </div>
                <ExternalLink size={16} className="ml-auto" />
              </button>
            </div>
          )}
        </div>
      ) : lead.status === 'lost' ? (
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center">
            <XCircle size={24} className="text-red-600 mr-3" />
            <div>
              <p className="font-medium text-red-900">Lead Lost</p>
              <p className="text-sm text-red-700">
                This lead was marked as lost on {formatDate(lead.lostAt)}
              </p>
              {lead.lostReason && (
                <p className="text-sm text-red-700 mt-2">
                  Reason: {lead.lostReason}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center">
              <Clock size={24} className="text-yellow-600 mr-3" />
              <div>
                <p className="font-medium text-yellow-900">Not Yet Enrolled</p>
                <p className="text-sm text-yellow-700">
                  Current stage: <span className="font-medium">{currentStage.label}</span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Program Interest</h4>
              <p className="text-gray-900">{lead.programInterest || 'Not specified'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Desired Start Date</h4>
              <p className="text-gray-900">{lead.desiredStartDate ? formatDate(lead.desiredStartDate) : 'Not specified'}</p>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Notes</h4>
            <p className="text-gray-900">{lead.enrollmentNotes || 'No enrollment notes'}</p>
          </div>
          
          <div className="flex items-center space-x-4 pt-4 border-t">
            <button
              onClick={() => setShowConvertModal(true)}
              className="btn-primary flex items-center space-x-2 bg-green-600 hover:bg-green-700"
            >
              <GraduationCap size={18} />
              <span>Convert to Student</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Lost Reason Modal
  const renderLostReasonModal = () => {
    if (!showLostReasonModal) return null;
    
    const reasons = [
      'Chose competitor',
      'Price too high',
      'Location too far',
      'Schedule mismatch',
      'No longer interested',
      'Could not reach',
      'Other',
    ];
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-900">Mark Lead as Lost</h2>
            <p className="text-sm text-gray-500 mt-1">Please provide a reason for marking this lead as lost</p>
          </div>
          
          <div className="p-6 space-y-4">
            <div>
              <label className="form-label">Reason</label>
              <select
                className="form-input mb-4"
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
              >
                <option value="">Select a reason...</option>
                {reasons.map(reason => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="form-label">Additional Notes (optional)</label>
              <textarea
                className="form-input"
                rows="3"
                placeholder="Any additional details..."
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-100">
            <button
              onClick={() => {
                setShowLostReasonModal(false);
                setLostReason('');
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleMarkAsLost}
              disabled={markAsLostMutation.isPending || !lostReason}
              className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {markAsLostMutation.isPending ? 'Marking...' : 'Mark as Lost'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      {renderHeader()}
      
      {/* Quick Actions */}
      {renderQuickActions()}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto pb-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pb-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'contact' && renderContactDetails()}
        {activeTab === 'tours' && renderTourHistory()}
        {activeTab === 'enrollment' && renderEnrollmentInfo()}
      </div>

      {/* Modals */}
      <AddNoteModal
        isOpen={showAddNoteModal}
        onClose={() => setShowAddNoteModal(false)}
        onSubmit={handleAddNote}
        isLoading={addNoteMutation.isPending}
      />

      <TourScheduleModal
        isOpen={showTourModal}
        onClose={() => setShowTourModal(false)}
        onSubmit={handleScheduleTour}
        isLoading={scheduleTourMutation.isPending}
        staff={[]} // Pass actual staff list from API
      />

      <ConvertLeadModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        onSubmit={handleConvertLead}
        lead={lead}
        isLoading={false}
        classes={[]} // Pass actual classes list from API
      />

      <AssignLeadModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onSubmit={handleAssignLead}
        lead={lead}
        isLoading={assignLeadMutation.isPending}
        staff={[]} // Pass actual staff list from API
      />

      {renderLostReasonModal()}
    </div>
  );
};

// Helper icon component
const BookOpenIcon = ({ size, className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

export default LeadDetail;
